import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import useWebData from '../../../hooks/useWebData';
import usePleaseWait from '../../../hooks/usePleaseWait';
import { baseURL, tenantId } from '../../../config/api';
import { getDisabledVehicleLocationByUniqidForShareLocation, put_DisabledVehicleLocation } from '../../../services/disabledVehicleLocation';
import axios from 'axios';
import useConfirm from '../../../hooks/useConfirm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleRight } from '@fortawesome/free-solid-svg-icons';

const ShareLocation = () => {
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search)
    const uniqeid = queryParams?.get('id') ?? 0;

    const navigate = useNavigate();

    const { webData }: any = useWebData();
    const { pleaseWaitPopup, setPleasWaitPopup }: any = usePleaseWait();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isResponend, setIsResponed] = useState<boolean>(false);
    const [locationData, setLocationData] = useState<any | null>(null);
    const [addExtraInfo, setAddExtraInfo] = useState<boolean>(false);
    const [shareLocationData, setShareLocationData] = useState<any | null>({
        latitude: 0,
        longitude: 0,
        location: "",
        locationImage: "",
        comment: ""
    });

    const inputFileRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!uniqeid) {
            navigate(`not-found`, { replace: true });
        }

        if (uniqeid) {
            setIsLoading(true);
            setPleasWaitPopup(true);
            getDisabledVehicleLocationByUniqidForShareLocation(uniqeid, tenantId)
                .then(res => {
                    setIsResponed(false);
                    setShareLocationData({
                        latitude: res?.latitude,
                        longitude: res?.longitude,
                        location: res?.location,
                        locationImage: res?.locationImage,
                        comment: res?.comment
                    })
                    setIsLoading(false);
                    setPleasWaitPopup(false);
                    setLocationData(res);

                    if (res?.isResponded) {
                        navigate(`/public/share-location/not-available?id=${uniqeid}`, { replace: true });
                    }
                })
                .catch((error) => {
                    setIsResponed(false);
                    setLocationData(null);
                    setIsLoading(false);
                    setPleasWaitPopup(false);
                    if (error?.response?.status === 400) {
                        if (error?.response?.data === "Link is expired or invalid.") {
                            navigate(`/public/share-location/not-available?id=${uniqeid}`, { replace: true });
                        } else {
                            navigate(`not-found`, { replace: true });
                        }
                    }
                    console.log(error);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uniqeid])

    const onFileChangeCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        /*Selected files data can be collected here.*/
        const selectedFiles = e.target.files as FileList;
        if (selectedFiles.length !== 0) {
            const formData = new FormData();
            formData.append("ImageFile", selectedFiles[0]);

            if (!pleaseWaitPopup) {
                setPleasWaitPopup(true);
                axios
                    .post(`${baseURL}reservationFile/upload`, formData, {
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "multipart/form-data"
                        },
                        withCredentials: true,
                    })
                    .then((response) => {
                        setPleasWaitPopup(false);
                        setShareLocationData({ ...shareLocationData, locationImage: response.data });
                    })
                    .catch((error) => {
                        setPleasWaitPopup(false);
                        console.error("Error:", error);
                    });
            }
        }
    }

    const shareLocation = async () => {
        if (!uniqeid) {
            console.log("Unique ID is missing.");
            return;
        }

        const getLocation = () => {
            return new Promise((resolve, reject) => {
                if (!navigator.geolocation) {
                    reject("Geolocation is not supported by this browser.");
                }

                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;
                        resolve({ latitude, longitude });
                    },
                    (error) => {
                        reject(error);
                    }
                );
            });
        };

        try {
            // Check permission status first
            if (navigator.permissions) {
                const permissionStatus = await navigator.permissions.query({ name: "geolocation" });

                if (permissionStatus.state === "denied") {
                    setConfirmData({
                        title: 'Warning',
                        summary: "Please enable location services in your browser settings to share your location.",
                        confirmBtn: 'Ok',
                        isConfirm: (callback: any) => {
                            callback();
                        }
                    });

                    setConfirmPopup(true);
                    return;
                }
            }

            // Try to get location
            const { latitude, longitude }: any = await getLocation();

            setShareLocationData((prev: any) => ({
                ...prev,
                latitude,
                longitude
            }));

            if (!pleaseWaitPopup) {
                setPleasWaitPopup(true);

                await put_DisabledVehicleLocation(
                    {
                        ...shareLocationData,
                        latitude,
                        longitude
                    },
                    uniqeid,
                    tenantId
                );

                setPleasWaitPopup(false);
                setIsResponed(true);
            }
        } catch (error: any) {
            console.error("Error getting location:", error);
            let message = "";

            if (error?.code === 1) {
                // PERMISSION_DENIED
                message = "Location access denied. Please enable location to share your position.";
            } else if (error?.code === 2) {
                message = "Location unavailable. Please check your GPS or network connection.";
            } else if (error?.code === 3) {
                message = "Location request timed out. Please try again.";
            } else {
                message = "Unable to access location. Please try again later.";
            }

            if (message) {
                setConfirmData({
                    title: 'Warning',
                    summary: message,
                    confirmBtn: 'Ok',
                    isConfirm: (callback: any) => {
                        callback();
                    }
                });

                setConfirmPopup(true);
            }
        }
    }

    return (
        <div className="mt-[140px]">
            {
                isLoading
                    ? (
                        <div className="min-h-[calc(100vh-140px)] h-[calc(100vh-140px)] bg-[#F2F7FA] px-[1rem] py-[0.5rem] my-[1.5rem]">
                            <div
                                className="bg-white w-full h-full border border-[#ff6926] rounded-lg"
                                style={{
                                    borderColor: webData?.websiteHeaderColor
                                }}
                            >
                                <div className="h-full flex items-center justify-center gap-x-[2rem] px-[2rem] py-[1rem] min-h-[24rem]">
                                    <p className="text-sm text-gray-400">Loading...</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="min-h-[calc(100vh-82px)] bg-[#F2F7FA] px-[1rem] py-[0.5rem] my-[1.5rem]">
                                <div className="bg-white w-full h-full border border-[#E4E7EC] rounded-lg">

                                    <div className="flex flex-col lg:flex-row gap-x-[2rem] gap-y-[4rem] px-[0.5rem] lg:px-[2rem] py-[1rem]">
                                        <div className="w-full lg:flex-1 py-[0.75rem]">
                                            <div className="">
                                                <h5>
                                                    <span>Share Your Location – Request from {locationData?.companyName} for Booking - </span>
                                                    <strong>#{locationData?.bookingNumber || "-"}</strong>
                                                </h5>
                                            </div>

                                            {
                                                isResponend
                                                    ? (
                                                        <div className="space-y-[1.5rem]">
                                                            <div className="">
                                                                <h4 className="text-[1rem] font-semibold">Your location has been received. Thank you for sharing your location.</h4>
                                                            </div>

                                                            <button
                                                                type="button"
                                                                className="text-white text-center bg-[#ff6926] font-medium rounded-lg text-sm px-5 py-2.5 mb-2"
                                                                style={{
                                                                    backgroundColor: webData?.websiteHeaderColor
                                                                }}
                                                                onClick={() => {
                                                                    navigate('/');
                                                                }}
                                                            >
                                                                Back To Home
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="w-full lg:flex-1 py-[0.75rem] mt-[1.5rem]">
                                                                <button
                                                                    type="button"
                                                                    className="w-full md:w-auto hover:opacity-90 text-white bg-[#ff6926] font-semibold py-3 px-6 rounded-xl transition-all duration-200"
                                                                    style={{ backgroundColor: webData?.websiteHeaderColor }}
                                                                    onClick={shareLocation}
                                                                >
                                                                    Share My Location
                                                                </button>
                                                            </div>

                                                            <div className="mt-[1.25rem]">
                                                                <div
                                                                    className="flex flex-row items-center gap-1 w-full select-none cursor-pointer"
                                                                    onClick={() => setAddExtraInfo(!addExtraInfo)}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={addExtraInfo ? faAngleDown : faAngleRight}
                                                                        style={{ color: webData?.websiteHeaderColor }}
                                                                    />
                                                                    <h5
                                                                        style={{ color: webData?.websiteHeaderColor }}
                                                                        className="font-bold"
                                                                    >
                                                                        Add More Location Details
                                                                    </h5>
                                                                </div>

                                                                <hr className="w-full bg-[#E4E7EC] mb-2" />

                                                                {
                                                                    addExtraInfo && (
                                                                        <div className="w-full flex flex-col gap-4 px-4 py-4">
                                                                            <div className="w-full md:w-3/4">
                                                                                <label htmlFor="location" className="text-xs font-bold">Address (Physical Address)</label>
                                                                                <input
                                                                                    type="text"
                                                                                    name="location"
                                                                                    className="w-full px-3 py-2 border rounded-md outline-none text-sm"
                                                                                    value={shareLocationData?.location}
                                                                                    onChange={(e) => setShareLocationData({ ...shareLocationData, location: e.target.value })}
                                                                                />
                                                                            </div>

                                                                            <div className="w-full md:w-3/4">
                                                                                <label htmlFor="comment" className="text-xs font-bold">Comments</label>
                                                                                <textarea
                                                                                    rows={4}
                                                                                    name="comment"
                                                                                    className="w-full px-3 py-2 border rounded-md outline-none text-sm"
                                                                                    value={shareLocationData?.comment}
                                                                                    onChange={(e) => setShareLocationData({ ...shareLocationData, comment: e.target.value })}

                                                                                />
                                                                            </div>

                                                                            <div className="w-full flex flex-col gap-y-[0.25rem] md:w-3/4">
                                                                                <label htmlFor="comment" className="text-xs font-bold">Location Image</label>
                                                                                <button
                                                                                    type="button"
                                                                                    className="text-white bg-[#ff6926] px-4 py-2 rounded w-fit"
                                                                                    style={{
                                                                                        backgroundColor: webData?.websiteHeaderColor
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        if (inputFileRef.current) {
                                                                                            inputFileRef.current.value = ""
                                                                                        }
                                                                                        inputFileRef.current?.click()
                                                                                    }}
                                                                                >
                                                                                    {shareLocationData?.locationImage ? "Change Image" : "Upload Image"}
                                                                                </button>

                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    ref={inputFileRef}
                                                                                    className="hidden"
                                                                                    onChange={onFileChangeCapture}
                                                                                />

                                                                                {
                                                                                    shareLocationData?.locationImage && (
                                                                                        <img
                                                                                            src={`${baseURL}reservationFile/Get?name=${shareLocationData?.locationImage}`}
                                                                                            alt="location"
                                                                                            className="w-full h-[380px]"
                                                                                        />
                                                                                    )
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        </>
                                                    )
                                            }
                                        </div>

                                        <div className="w-1/4">{" "}</div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )
            }
        </div>
    )
}

export default ShareLocation

